"use client";

import { useRef } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";

import styles from "./HomepageHero.module.scss";
import { HomepageHeroLayoutProps } from "./HomepageHero.types";
import HomepageHeroTabs from "./HomepageHeroTabs";

const HomepageHeroMobile = ({
  data,
  setCoreFade,
  newCore,
  titleSection,
  benefitsSection,
  imagesSection,
}: HomepageHeroLayoutProps) => {
  const { subheading, pill, tabs } = data;
  const dotRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(max-width: 1279px)", () => {
      const contH = containerRef.current?.clientHeight ?? 0;
      const triggerTimeline = () => {
        const tl = gsap.timeline();
        tl.to(dotRef.current, { top: "calc(100% - 105px)", duration: 0.6, ease: "none" }, 0);
        tl.to(
          imgRef.current,
          {
            transform: "scale(1)",
            duration: 0.6,
            ease: "none",
            onComplete: () => {
              gsap.killTweensOf(imgRef.current);
              gsap.killTweensOf(dotRef.current);
              if (dotRef.current) dotRef.current.style.display = "none";
            },
          },
          0
        );
      };

      gsap.to(dotRef.current, {
        top: "calc(100% - 105px)",
        onComplete: () => {
          gsap.killTweensOf(dotRef.current);
          if (dotRef.current) dotRef.current.style.display = "none";
        },
        scrollTrigger: {
          trigger: ".badge-container",
          start: "top-=124 top",
          end: `+=${contH}`,
          scrub: 1,
          onUpdate: (self) => {
            const w = window.innerWidth;
            const startPx = w >= 1024 ? 60 : w >= 768 ? 50 : 35;
            const endPx = w >= 1024 ? 330 : w >= 768 ? 290 : 230;
            const scrollPx = self.progress * contH;
            gsap.set(dotRef.current, {
              opacity: scrollPx >= startPx && scrollPx <= endPx ? 0 : 1,
            });
            if (self.scroll() > contH / 2.8) triggerTimeline();
          },
        },
      });
    });
    return () => mm.revert();
  }, []);

  return (
    <div>
      <div className="w-[220px] md:w-[340px] mx-auto pt-10 md:pt-[77px]">
        <Picture src={pill} alt="" role="presentation" loading="eager" fetchPriority="high" />
      </div>
      <div ref={containerRef} className="relative z-[1] pt-10 md:pt-[77px] pb-8">
        <div ref={dotRef} className={styles.dot} />
        <div className={styles.line1} />
        {titleSection}
        <div className={styles.line2} />
        <div className="max-w-[288px] md:max-w-[445px] pt-[72px] md:pt-[120px] pb-20 md:pb-36 mx-auto">
          <TextElementNova
            text={subheading}
            element="h2"
            className="Title-20-Super-Normal text-center text-nova-base-white"
          />
          <div className={styles.line3} />
        </div>
        {imagesSection(imgRef)}
        <div className={styles.line4} />
      </div>
      <HomepageHeroTabs tabs={tabs} setter={setCoreFade} active={newCore} />
      <div className="pt-6">{benefitsSection}</div>
    </div>
  );
};

export default HomepageHeroMobile;
